/**
 * AssistantMarkdown — AI 回复正文的统一渲染器。
 *
 * 全产品所有 AI 对话面共用：主会话面板、文档审阅工作台侧栏、起草工作台、
 * 尽调信息收集。此前只有主会话面板接了 Markdown，其余三处直出纯文本，AI 输出
 * 的条款编号、加粗、列表在律师眼里就是一堆 `**` `-` `#` 符号，看起来像界面
 * 渲染故障。渲染规则收敛到这一个组件，新增对话面引用即可，不会再次分叉。
 *
 * 三项约定：
 *  - 表格与删除线等 GFM 语法启用（`remarkGfm`）——条款对比、风险清单在法律
 *    场景里高频出现，缺了表格语法会退化成一堆竖线。
 *  - 链接一律新标签页打开并加 `noopener noreferrer`：跟随一个来源链接不应
 *    冲掉律师正在进行的对话。
 *  - 正文经 `stripEngineMetadata` 清洗，把引擎附带的结构化块（如
 *    `<!-- RISK_METADATA ... -->`）挡在可见正文之外。流式途中可能出现半截的
 *    开标签，该函数已按未闭合注释处理。
 *
 * 流式与终态共用同一渲染路径：token 边界处的半截 Markdown 由 react-markdown
 * 按字面量渲染，随后续 token 落位自然成形。若只在终态渲染 Markdown，律师会
 * 先看一屏符号、再在最后一瞬间整体跳变成排版结果。
 *
 * 两个保真 prop（默认值即聊天面旧行为，既有消费方零影响）：
 *  - `stripMetadata={false}`：Prompt 查看场景。content 是律师存储的原文，
 *    注释块与尾部 `<!--` 是原文的一部分，逐字保真优先于清洗。
 *  - `breaks`：Prompt 查看场景传 true，单换行按作者真实排版渲染为换行
 *    （`remark-breaks`）；聊天面保持 GFM 默认的单换行折叠。
 */

import { forwardRef, memo, useMemo } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import remarkBreaks from "remark-breaks";
import { stripEngineMetadata } from "../../utils";
import {
  CITATION_MARKER_ATTR,
  rehypeCitationMarkers,
} from "../../utils/rehypeCitationMarkers";
import styles from "../../styles/assistant-markdown.module.css";

/** 把引用标记 span 的 children（react-markdown 渲染出的字符串/数组）拍平成
 * 一段纯文本，用于重建原始标记全文（如 "[#1,#2]"）。防御式处理数组形态，
 * 正常情况下 rehypeCitationMarkers 只产出单个文本子节点。 */
function flattenMarkerText(node: React.ReactNode): string {
  if (typeof node === "string") return node;
  if (typeof node === "number") return String(node);
  if (Array.isArray(node)) return node.map(flattenMarkerText).join("");
  return "";
}

export interface AssistantMarkdownProps {
  /** AI 回复原文（Markdown）。可以是流式途中的半截内容。 */
  content: string;
  /** 附加到容器上的类名（各面板自己的排版上下文，如气泡内边距）。 */
  className?: string;
  /** 流式光标等尾随节点，渲染在正文之后、容器之内。 */
  children?: React.ReactNode;
  /** 是否清洗引擎结构化块（默认 true——聊天面旧行为）。 */
  stripMetadata?: boolean;
  /** 单换行是否渲染为换行（默认 false——聊天面 GFM 折叠行为）。 */
  breaks?: boolean;
  /**
   * 点击正文里的 `[#N]` 引用标记。不传时标记按纯文本渲染（既有行为）。
   * 只有编号能在本轮引用清单里反查得到时才成为可点击标记，反查不到的编号保持
   * 纯文本——不做点了没反应的死链接。
   */
  onCitationClick?: (blockIndex: number) => void;
  /** 本轮引用清单里存在的编号；配合 `onCitationClick` 决定哪些标记可点击。 */
  resolvableBlockIndexes?: ReadonlySet<number>;
  "data-testid"?: string;
}

// memo（评审 V15）：宿主组件里与正文无关的状态更新（如删除确认输入框逐键
// 输入）不再触发多 KB Markdown 的重新解析。流式场景 content/children 每帧
// 变化，浅比较必然失配照常重渲染，行为不变。
const AssistantMarkdown = memo(
  forwardRef<HTMLDivElement, AssistantMarkdownProps>(function AssistantMarkdown(
    {
      content,
      className,
      children,
      stripMetadata = true,
      breaks = false,
      onCitationClick,
      resolvableBlockIndexes,
      "data-testid": testId,
    },
    ref,
  ) {
    // 只有宿主提供了跳转能力时才走标记改写，避免给不接定位的面板（如 Prompt
    // 查看）平添一层树遍历。
    const rehypePlugins = useMemo(
      () => (onCitationClick ? [rehypeCitationMarkers] : undefined),
      [onCitationClick],
    );
    const markdownComponents = useMemo(
      () => ({
        a: ({ children: linkChildren, ...props }: { children?: React.ReactNode }) => (
          <a {...props} target="_blank" rel="noopener noreferrer">
            {linkChildren}
          </a>
        ),
        span: ({ children: spanChildren, ...props }: { children?: React.ReactNode }) => {
          const raw = (props as Record<string, unknown>)[CITATION_MARKER_ATTR];
          const blockIndex = typeof raw === "string" ? Number.parseInt(raw, 10) : NaN;
          if (!Number.isInteger(blockIndex)) return <span {...props}>{spanChildren}</span>;
          // rehypeCitationMarkers 把原始标记全文（如 "[#1,#2]"）放进 children；
          // 合并标记视觉上展示全部编号（去掉方括号与 # 号，逗号分隔），点击行为
          // 仍按 blockIndex（首编号）跳转——与既有 resolvable 判定口径一致。
          const rawText = flattenMarkerText(spanChildren);
          const badgeLabel = (rawText.match(/\d+/g) ?? [String(blockIndex)]).join(",");
          const clickable =
            onCitationClick &&
            (!resolvableBlockIndexes || resolvableBlockIndexes.has(blockIndex));
          // 真实文本节点重建原始标记全文（如 "[#1,#2]"，flattenMarkerText 拿不到
          // 字符串时兜底用 blockIndex 拼一个）——数字徽标的可见内容由下面
          // data-badge-label 经 CSS `::after` 渲染，CSS 伪元素内容不进
          // `.textContent`，因此不能拿它承载「按格式复制」需要的原文；反过来
          // 这个真实文本节点本身 aria-hidden（不可点击态直接把外层 span 也
          // aria-hidden——它不是交互元素，屏幕阅读器没有必要逐个播报），可见
          // 数字与「按格式复制」读到的内容各有一个来源，不在 textContent 里
          // 重复拼接，也不会被朗读两遍。可点击态改用 aria-label 播报干净的
          // 编号（"1,2"），不读原始方括号写法。
          const srText = (
            <span className={styles.srOnly} aria-hidden="true">
              {rawText || `[#${blockIndex}]`}
            </span>
          );
          if (clickable) {
            return (
              <button
                type="button"
                className={`${styles.citationBadge} ${styles.citationBadgeClickable}`}
                data-testid={`citation-marker-${blockIndex}`}
                data-badge-label={badgeLabel}
                aria-label={`定位引用 ${badgeLabel}`}
                onClick={() => onCitationClick(blockIndex)}
              >
                {srText}
              </button>
            );
          }
          return (
            <span
              {...props}
              className={`${styles.citationBadge} ${styles.citationBadgeStatic}`}
              aria-hidden="true"
              data-badge-label={badgeLabel}
            >
              {srText}
            </span>
          );
        },
      }),
      [onCitationClick, resolvableBlockIndexes],
    );

    return (
      <div
        ref={ref}
        className={className ? `${styles.markdown} ${className}` : styles.markdown}
        data-testid={testId}
      >
        <ReactMarkdown
          remarkPlugins={breaks ? [remarkGfm, remarkBreaks] : [remarkGfm]}
          rehypePlugins={rehypePlugins}
          components={markdownComponents}
        >
          {stripMetadata ? stripEngineMetadata(content) : content}
        </ReactMarkdown>
        {children}
      </div>
    );
  }),
);

export default AssistantMarkdown;
